import React from 'react';
import { Input, Textarea, Select, SelectItem } from '@heroui/react';
import type { DynamicFieldProps } from './types';

export const DynamicField: React.FC<DynamicFieldProps> = ({
  name,
  schema,
  value,
  onChange,
  required = false,
  disabled = false,
}) => {
  const renderStringField = () => {
    // Handle enum (select dropdown)
    if (schema.enum) {
      return (
        <Select
          selectedKeys={value ? [value] : []}
          onSelectionChange={(keys) => onChange(Array.from(keys)[0] || '')}
          placeholder="선택하세요"
          isDisabled={disabled}
          isRequired={required}
          classNames={{
            trigger: "bg-white border border-gray-300"
          }}
        >
          {schema.enum.map((option) => (
            <SelectItem key={option}>
              {option}
            </SelectItem>
          ))}
        </Select>
      );
    }

    // Handle pattern validation (e.g., for Instagram media ID)
    if (schema.pattern) {
      return (
        <Input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={getPlaceholderFromPattern(schema.pattern, schema.description)}
          isDisabled={disabled}
          isRequired={required}
          classNames={{
            input: "bg-white",
            inputWrapper: "bg-white border border-gray-300"
          }}
        />
      );
    }

    // Handle textarea for long text (maxLength > 100)
    if (schema.maxLength && schema.maxLength > 100) {
      return (
        <Textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={schema.description}
          isDisabled={disabled}
          isRequired={required}
          maxLength={schema.maxLength}
          minRows={3}
        />
      );
    }

    // Default text input
    return (
      <Input
        type="text"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={schema.description}
        isDisabled={disabled}
        isRequired={required}
        classNames={{
          input: "bg-white",
          inputWrapper: "bg-white border border-gray-300"
        }}
      />
    );
  };

  const renderNumberField = () => {
    return (
      <Input
        type="number"
        value={value !== undefined && value !== null ? String(value) : ''}
        onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
        placeholder={schema.description}
        isDisabled={disabled}
        isRequired={required}
        min={schema.minimum}
        max={schema.maximum}
        classNames={{
          input: "bg-white",
          inputWrapper: "bg-white border border-gray-300"
        }}
      />
    );
  };

  const renderBooleanField = () => {
    return (
      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          checked={value || false}
          onChange={(e) => onChange(e.target.checked)}
          className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
          disabled={disabled}
        />
        <span className="text-sm text-gray-700">
          {schema.description || '활성화'}
        </span>
      </div>
    );
  };

  const renderField = () => {
    switch (schema.type) {
      case 'string':
        return renderStringField();
      case 'number':
      case 'integer':
        return renderNumberField();
      case 'boolean':
        return renderBooleanField();
      default:
        return (
          <div className="text-gray-500 italic">
            지원되지 않는 필드 타입: {schema.type}
          </div>
        );
    }
  };

  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-gray-700">
        {getFieldLabel(name, schema.description)}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {renderField()}
    </div>
  );
};

// Helper functions
function getFieldLabel(name: string, description?: string): string {
  // Convert camelCase to readable format
  const readable = name.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
  
  // Use description if available, otherwise use the readable name
  if (description) {
    return description;
  }
  
  // Provide Korean translations for common field names
  const translations: Record<string, string> = {
    'Account Id': '계정 ID',
    'Target User Id': '대상 사용자 ID',
    'Media Id': '미디어 ID',
    'Post Id': '게시물 ID',
    'Video Id': '동영상 ID',
    'Message': '메시지',
  };
  
  return translations[readable] || readable;
}

function getPlaceholderFromPattern(pattern: string, description?: string): string {
  if (description) return description;
  
  // Provide examples based on common patterns
  if (pattern.includes('[0-9]+_[0-9]+')) {
    return '예: 1234567890_1234567890';
  }
  
  if (pattern.includes('^[0-9]+$')) {
    return '숫자만 입력하세요';
  }
  
  return '패턴에 맞게 입력하세요';
}